import {
  Activity,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Circle,
  Dumbbell,
  PenLine,
  Heart,
  Wrench,
  Plus,
  Utensils,
} from "lucide-react";
import type { DiaryComposeIntent } from "../../components/DiaryFeed";
import type { MissionAction, WeeklyMissionPayload } from "../../api";
import {
  missionAction,
  missionProgressLabel,
  missionTaskTitle,
  sortMissionTasks,
} from "../../components/missionModel";
import {
  dimensions,
  dimensionFor,
  type DimensionKey,
} from "./desktop/dimensions";
import { useGrowthRecords } from "./desktop/useGrowthRecords";
type Props = {
  displayName?: string;
  hp: number | null;
  ep: number | null;
  dimensionScores: Record<string, number>;
  mission: WeeklyMissionPayload | null;
  missionLoading: boolean;
  missionError: string | null;
  onRetry: () => void;
  onRecord: () => void;
  onQuickRecord: (type: DiaryComposeIntent) => void;
  onOpenTasks: () => void;
  onOpenRecords: () => void;
  onOpenTask: (id: string | number) => void;
  onOpenAction: (action: MissionAction) => void;
  onOpenDimension: (key: DimensionKey) => void;
};
const percent = (n: number | null) => Math.max(0, Math.min(100, n ?? 0));
export default function WebHome(props: Props) {
  const {
    hp,
    ep,
    dimensionScores,
    mission,
    missionLoading,
    missionError,
    onOpenTasks,
    onRecord,
    onOpenRecords,
    onOpenDimension,
  } = props;
  const recent = useGrowthRecords();
  const tasks = sortMissionTasks(
    (mission?.groups || []).flatMap((g) =>
      g.tasks.map((t) => ({ ...t, dimension: t.dimension || g.key })),
    ),
  );
  const pending = tasks.filter((t) => t.status !== "completed" && !t.archived);
  const completed = tasks.filter((t) => t.status === "completed");
  return (
    <section className="desktop-home record-first-home" aria-label="网页端成长工作台">
      <header className="desktop-page-header">
        <div>
          <p className="home-date">{new Intl.DateTimeFormat("zh-CN", { dateStyle: "full" }).format(new Date())}</p>
          <h1>{props.displayName ? `${props.displayName}，` : ""}把今天记下来</h1>
          <p>从随手一记开始，看见生活的六个方向。</p>
        </div>
        <div className="home-vitals" aria-label="当前身心状态">
          <button onClick={() => onOpenDimension("body")}><Activity size={17} /><span>身体能量<strong>{hp ?? "—"}<small> / 100</small></strong></span></button>
          <button onClick={() => onOpenDimension("heart")}><Heart size={17} /><span>心理能量<strong>{ep ?? "—"}<small> / 100</small></strong></span></button>
        </div>
      </header>

      <section className="home-capture" aria-label="随时记录">
        <button className="home-capture-prompt" onClick={() => props.onQuickRecord("note")}>
          <span className="home-capture-icon"><PenLine size={26} /></span>
          <span><strong>此刻，有什么想记下来？</strong><small>一个念头、一点心情，或今天的小小进步。</small></span>
          <span className="home-capture-cta">随手记一笔 <ArrowRight size={18} /></span>
        </button>
        <div className="home-capture-options" aria-label="快捷记录类型">
          <button onClick={() => props.onQuickRecord("mood")}><Heart size={18} />记心情</button>
          <button onClick={() => props.onQuickRecord("note")}><BookOpen size={18} />记笔记</button>
          <button onClick={() => props.onQuickRecord("tech")}><Wrench size={18} />记实践</button>
          <button onClick={() => props.onOpenAction({ type: "meal", label: "记录饮食" })}><Utensils size={18} />记饮食</button>
          <button onClick={() => props.onOpenAction({ type: "exercise", label: "记录运动" })}><Dumbbell size={18} />记运动</button>
          <button onClick={onRecord}><Plus size={18} />更多记录</button>
        </div>
      </section>

      <section className="home-six-dimensions" aria-labelledby="home-six-title">
        <div className="desktop-section-heading">
          <div><h2 id="home-six-title">生活的六个维度</h2><p>记录当下，也看见更完整的自己。</p></div>
          <span className="home-dimension-note">根据行动与各模块数据更新</span>
        </div>
        <div className="home-dimension-grid">
          {dimensions.map((d) => {
            const raw = dimensionScores[d.key] ?? (d.key === "body" ? hp : d.key === "heart" ? ep : null);
            const value = raw == null || !Number.isFinite(raw) ? null : Math.round(percent(raw));
            const Icon = d.icon;
            return (
              <button key={d.key} className="home-dimension-card" onClick={() => onOpenDimension(d.key)} aria-label={`${d.glyph} · ${d.name}，${value === null ? "暂无评分" : `${value}分`}`}>
                <span className="home-dimension-top"><span className="home-dimension-glyph">{d.glyph}</span><Icon size={20} /></span>
                <strong>{d.name}</strong><span className="home-dimension-hint">{d.hint}</span>
                <span className="home-dimension-score"><b>{value ?? "—"}</b><small>{value === null ? "待积累" : "/ 100"}</small><ArrowRight size={16} /></span>
                <span className="desktop-meter" aria-hidden="true"><i style={{width: `${value ?? 0}%`}} /></span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="home-followthrough">
          <section className="desktop-panel home-recent">
            <div className="desktop-section-heading">
              <div>
                <h2>最近记录</h2>
                <p>每一笔，都是生活留下的足迹。</p>
              </div>
              <button className="desktop-text-button" onClick={onOpenRecords}>
                查看全部
                <ArrowRight size={16} />
              </button>
            </div>
            {recent.error && (
              <div className="desktop-notice" role="alert">
                {recent.error}
                <button onClick={recent.reload}>重试</button>
              </div>
            )}
            {recent.loading ? (
              <p role="status" className="desktop-muted">
                正在加载记录…
              </p>
            ) : (
              recent.records.slice(0, 4).map((r) => {
                const Icon =
                  r.kind === "meal"
                    ? Utensils
                    : r.kind === "exercise"
                      ? Dumbbell
                      : BookOpen;
                return (
                  <button
                    key={r.key}
                    className="desktop-recent-row"
                    onClick={onOpenRecords}
                  >
                    <span className={`desktop-record-icon ${r.kind}`}>
                      <Icon size={21} />
                    </span>
                    <span>
                      <strong>{r.title}</strong>
                      <small>
                        {r.label} · {r.day}
                      </small>
                      <p>{r.content}</p>
                    </span>
                  </button>
                );
              })
            )}
            {!recent.loading && !recent.records.length && !recent.error && (
              <p className="desktop-empty">
                还没有记录，从上方“随手记一笔”开始。
              </p>
            )}
          </section>
          <section className="desktop-panel home-actions">
            <div className="desktop-section-heading">
              <div>
                <h2>本周行动</h2>
                <p>从小的行动开始，持续积累，成为更好的自己。</p>
              </div>
              {mission && (
                <div className="desktop-progress-summary">
                  <strong>
                    {mission.completed} / {mission.total}
                  </strong>
                  <span>已完成</span>
                  <progress
                    aria-label="本周任务完成进度"
                    value={percent(mission.pct)}
                    max={100}
                  />
                  <small>{Math.round(percent(mission.pct))}%</small>
                </div>
              )}
            </div>
            {missionError ? (
              <div className="desktop-notice" role="alert">
                {missionError}
                <button onClick={props.onRetry}>重试</button>
              </div>
            ) : missionLoading && !mission ? (
              <p role="status" className="desktop-empty">
                正在加载本周行动…
              </p>
            ) : (
              <>
                <div className="desktop-action-list">
                  {pending.slice(0, 3).map((task) => {
                    const d = dimensionFor(task.dimension);
                    const Icon = d.icon;
                    return (
                      <button
                        key={task.id}
                        className="desktop-action-row"
                        onClick={() =>
                          task.verification === "auto"
                            ? props.onOpenAction(missionAction(task))
                            : props.onOpenTask(task.id)
                        }
                      >
                        <Circle size={20} className="desktop-muted" />
                        <span className="desktop-action-copy">
                          <strong>{missionTaskTitle(task)}</strong>
                          <small>
                            <Icon size={15} color={d.color} />
                            <span style={{ color: d.color }}>
                              {d.glyph}
                            </span> ·{" "}
                            {task.estimated_minutes
                              ? `约 ${task.estimated_minutes} 分钟`
                              : "本周行动"}
                          </small>
                        </span>
                        <span className="desktop-action-progress">
                          {missionProgressLabel(task)}
                        </span>
                        <ArrowRight size={18} />
                      </button>
                    );
                  })}
                </div>
                {!pending.length && (
                  <div className="desktop-empty">
                    <CheckCircle2 size={30} />
                    <p>
                      {mission?.total
                        ? "本周任务已完成，给自己一点鼓励。"
                        : "还没有本周任务，去任务中心安排一个小行动。"}
                    </p>
                    <button
                      className="desktop-text-button"
                      onClick={onOpenTasks}
                    >
                      打开任务中心 <ArrowRight size={16} />
                    </button>
                  </div>
                )}
                <div className="desktop-action-footer">
                  <details>
                    <summary>
                      <ChevronDown size={16} />
                      已完成 {completed.length} 项
                    </summary>
                    {completed.map((t) => (
                      <p key={t.id}>
                        <CheckCircle2 size={15} />
                        {missionTaskTitle(t)}
                      </p>
                    ))}
                  </details>
                  <button className="desktop-text-button" onClick={onOpenTasks}>
                    {pending.length > 3
                      ? `查看全部 ${pending.length} 项待办`
                      : "任务中心"}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}
          </section>
      </div>
    </section>
  );
}
